/**
 * Generate a document from one of the firm's own precedents (VALE-928).
 *
 * Opened from `Generate from precedent`, beside `+ Add Documents`, because that
 * is what this is: another way to put a document on the matter. The generated
 * `.docx` and PDF land in the list as ordinary matter files, which is why there
 * is no separate home for them. (There was one: a permanently-mounted panel
 * below the list, empty for every firm that had never uploaded a precedent. It
 * duplicated the list it sat under and this replaced it.)
 *
 * ## The precedent declares what it asks for
 *
 * This is the whole shape of the drawer, and it is why a fixed form would be
 * wrong. The backend reads each precedent's `.docx` and reports what it
 * references, so a plain letterhead asks for an addressee and nothing else -
 * pick, Generate, done, while an affidavit carrying clause toggles and an
 * exhibit block asks for both. A fixed form would put a clause toggle on a
 * precedent with no clauses and an exhibit picker on one with nowhere to render
 * them: required questions whose answers cannot reach the document.
 *
 * **The signatory is deliberately not among them.** `SignatoryAssignmentService`
 * already resolves it from the acting solicitor and the firm's own preference,
 * so a picker here would be a second definition of a rule the firm has already
 * configured, and the two would be free to disagree.
 */

'use client';

import { useMemo, useState } from 'react';
import { FilePlus2, FileText, Loader2Icon } from 'lucide-react';
import { toast } from '@/lib/toast';

import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@/components/ui/sheet';
import { LabelledToggleRow } from '@/components/ui/labelled-toggle-row';
import { ListItemSkeleton } from '@/components/ui/skeleton';
import { getMergeFieldLabel } from '@/lib/services/precedents/merge-field-labels';
import { exhibitLetter } from '@/lib/services/precedent-documents/exhibit-letter';
import {
  useGeneratePrecedentDocument,
  usePrecedentDocumentOptions,
  usePrecedentDocuments
} from '@/lib/services/precedent-documents';
import type {
  PrecedentDocument,
  PrecedentDocumentOptions,
  PrecedentExhibitOption
} from '@/lib/services/precedent-documents';

type ExhibitSelection = { fileId: number; description: string }[];

export function GeneratePrecedentDocumentDrawer({
  matterId,
  open,
  onOpenChange
}: {
  matterId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  // Fetched only while open: this read opens every one of the firm's precedents
  // out of S3 to discover its clauses, so it is markedly more expensive than the
  // list and carries its own query key for that reason.
  const {
    data: options,
    isLoading,
    isError
  } = usePrecedentDocumentOptions(matterId, open);
  const { data: existing } = usePrecedentDocuments(matterId);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col overflow-hidden p-0 sm:w-[34rem] sm:max-w-none"
      >
        <div className="border-border-subtle border-b py-4 pr-14 pl-6">
          <SheetTitle>Generate from precedent</SheetTitle>
          <SheetDescription className="mt-0.5">
            Merge this matter&apos;s details into one of your firm&apos;s own
            precedents. The result is saved to this matter&apos;s documents as a
            PDF, which you can edit in Word at any time.
          </SheetDescription>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="space-y-3">
              <p className="text-muted-foreground text-sm">
                Reading your firm&apos;s precedents…
              </p>
              <ListItemSkeleton />
              <ListItemSkeleton />
            </div>
          ) : isError || !options ? (
            <Banner
              tone="critical"
              title="Your precedents could not be loaded"
              line="Close this and try again in a moment. If it keeps happening, check the precedents still open in Templates → Documents."
            />
          ) : (
            <GeneratePrecedentDocumentBody
              // Re-keyed on the *offer* rather than a fetch timestamp, so a
              // background refetch returning the same precedents and documents
              // leaves a half-filled form alone while a genuinely different
              // offer resets it.
              key={offerFingerprint(options)}
              matterId={matterId}
              options={options}
              existing={existing?.documents ?? []}
              onClose={() => onOpenChange(false)}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function GeneratePrecedentDocumentBody({
  matterId,
  options,
  existing,
  onClose
}: {
  matterId: number;
  options: PrecedentDocumentOptions;
  existing: PrecedentDocument[];
  onClose: () => void;
}) {
  const generate = useGeneratePrecedentDocument(matterId);

  const [templateId, setTemplateId] = useState<number | null>(
    options.precedents[0]?.templateId ?? null
  );
  const [recipientContactId, setRecipientContactId] = useState<number | null>(
    null
  );
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [exhibits, setExhibits] = useState<ExhibitSelection>([]);

  const precedent = useMemo(
    () => options.precedents.find((option) => option.templateId === templateId),
    [options.precedents, templateId]
  );

  /**
   * The document this generation will replace, if there already is one.
   *
   * The backend's replacement key is `(matter, precedent, addressee)`, so a
   * *fresh* Generate landing on an existing pair overwrites it exactly as
   * Regenerate does. That is correct behaviour and it was invisible, the old
   * dialog said nothing, so the first a lawyer knew of it was a document they
   * had not meant to touch being archived. It is said out loud below.
   */
  const replacing = useMemo(
    () => findReplacedDocument(existing, templateId, recipientContactId),
    [existing, templateId, recipientContactId]
  );

  /**
   * The matter's documents, minus the ones this generation is about to replace.
   *
   * A generated document is an ordinary matter file, so the options endpoint
   * offers it like any other, right in general (a firm may well exhibit a
   * generated inventory in its affidavit) and wrong for exactly one file: the
   * document this generation overwrites. Exhibiting it would make the instrument
   * swear to a superseded draft of itself, and the very call that renders it
   * archives the copy it names.
   *
   * Keyed on `replacing`, so it covers the fresh-Generate path as well as
   * Regenerate, filtering only one leaves the same defect reachable from the
   * other.
   */
  const exhibitCandidates = useMemo(
    () => exhibitCandidatesExcludingOwn(options.exhibitCandidates, replacing),
    [options.exhibitCandidates, replacing]
  );

  if (options.precedents.length === 0) {
    /**
     * **The firm has uploaded no precedents.**
     *
     * The panel this replaced rendered a permanent "Nothing generated yet" empty
     * state on every matter of every such firm, with a Generate button that
     * dead-ended into exactly this message. Behind a CTA the state is reached
     * only by asking for it, and the answer names the one place that fixes it.
     */
    return (
      <div className="space-y-4">
        <Banner
          tone="neutral"
          title="Your firm has no precedents yet"
          line="Upload a Word document under Templates → Documents, then come back here to generate it against this matter."
        />
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  const toggleExhibit = (candidate: PrecedentExhibitOption) => {
    setExhibits((current) =>
      current.some((exhibit) => exhibit.fileId === candidate.fileId)
        ? current.filter((exhibit) => exhibit.fileId !== candidate.fileId)
        : // Appended, not inserted: exhibits are lettered by position, so the
          // order the lawyer ticks them in *is* the order A, B, C.
          [...current, { fileId: candidate.fileId, description: '' }]
    );
  };

  const describeExhibit = (fileId: number, description: string) =>
    setExhibits((current) =>
      current.map((exhibit) =>
        exhibit.fileId === fileId ? { ...exhibit, description } : exhibit
      )
    );

  const undescribedExhibits = exhibits.filter(
    (exhibit) => exhibit.description.trim().length === 0
  );
  const canGenerate =
    templateId !== null &&
    undescribedExhibits.length === 0 &&
    !generate.isPending;

  const onGenerate = async () => {
    if (templateId === null) return;
    try {
      const result = await generate.mutateAsync({
        templateId,
        ...(recipientContactId !== null ? { recipientContactId } : {}),
        ...(Object.keys(flags).length > 0 ? { flags } : {}),
        ...(exhibits.length > 0
          ? {
              exhibits: exhibits.map((exhibit) => ({
                fileId: exhibit.fileId,
                description: exhibit.description.trim()
              }))
            }
          : {})
      });

      if (result.missingOptional.length > 0) {
        // Not an error, every field on a firm's own precedent is optional by
        // design, so the document generated. But a lawyer about to send it
        // should know which merge fields came back blank, because a blank in a
        // sentence is far harder to spot than a missing one.
        //
        // The names go through the shared label helper rather than being joined
        // raw (VALE-788): this used to read `recipient.address, lawFirm.address`
        // where every other surface in the product says "Beneficiary address".
        // The toast is no longer the only record either, so it can afford to be
        // the short version: the same list is now on the document itself, in the
        // provenance panel, for the lawyer who dismissed this.
        toast.warning(
          `Generated with ${result.missingOptional.length} field${result.missingOptional.length === 1 ? '' : 's'} left blank`,
          {
            description: `${result.missingOptional
              .map(getMergeFieldLabel)
              .join(', ')}. You can see this again on the document itself.`
          }
        );
      } else {
        toast.success('Document generated', {
          description: 'It is now in this matter’s documents.'
        });
      }
      onClose();
    } catch (error) {
      toast.error('The document could not be generated', {
        description:
          error instanceof Error
            ? error.message
            : 'Check the precedent opens in Word and try again.'
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="precedent">Precedent</Label>
        <Select
          value={templateId === null ? undefined : String(templateId)}
          onValueChange={(value) => {
            setTemplateId(Number(value));
            // The clause toggles belong to the precedent that carries them, so
            // switching precedent must not carry the previous one's answers
            // across, a same-named clause in two precedents is not the same
            // clause. The exhibits go for a related reason: the new precedent
            // may have nowhere to render them.
            setFlags({});
            setExhibits([]);
          }}
        >
          <SelectTrigger id="precedent">
            <SelectValue placeholder="Choose a precedent" />
          </SelectTrigger>
          <SelectContent>
            {options.precedents.map((option) => (
              <SelectItem
                key={option.templateId}
                value={String(option.templateId)}
              >
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Every section below is conditional on what the chosen precedent
          actually references. A precedent that declares none of them renders
          this drawer as a single picker and a button. */}

      {precedent?.usesRecipient && (
        <div className="space-y-2">
          <Label htmlFor="recipient">Addressed to</Label>
          <Select
            value={
              recipientContactId === null
                ? undefined
                : String(recipientContactId)
            }
            onValueChange={(value) => setRecipientContactId(Number(value))}
          >
            <SelectTrigger id="recipient">
              <SelectValue placeholder="Choose who this is for" />
            </SelectTrigger>
            <SelectContent>
              {options.recipients.map((option) => (
                <SelectItem
                  key={option.contactId}
                  value={String(option.contactId)}
                >
                  {option.name}
                  {option.role ? ` - ${option.role}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Deliberately does NOT name the role. The backend falls back to the
              matter's first legal personal representative, and whether that
              person is an executor or an applicant for letters of
              administration is derived per matter (`isAdministrationMatter`).
              Writing either word here would restate a rule the system already
              owns, and be wrong on half of all matters. */}
          <p className="text-muted-foreground text-sm">
            This precedent names an addressee. Left unset, it goes to the first
            legal personal representative on this matter.
          </p>
        </div>
      )}

      {precedent && precedent.flags.length > 0 && (
        <div className="space-y-3">
          <div>
            <Label>Optional clauses</Label>
            <p className="text-muted-foreground text-sm">
              Sections your precedent switches in or out. Anything left off is
              simply not included.
            </p>
          </div>
          <div className="space-y-3">
            {precedent.flags.map((flag) => (
              <LabelledToggleRow
                key={flag.name}
                id={`flag-${flag.name}`}
                label={flag.label}
                description={flag.description}
                checked={flags[flag.name] === true}
                onCheckedChange={(checked) =>
                  setFlags((current) => ({
                    ...current,
                    [flag.name]: checked
                  }))
                }
              />
            ))}
          </div>
        </div>
      )}

      {precedent?.usesExhibits && (
        <div className="space-y-3">
          <div>
            <Label>Exhibits</Label>
            <p className="text-muted-foreground text-sm">
              Documents on this matter to exhibit. They are lettered A, B, C in
              the order you tick them, and each needs the description your
              certificate should identify it by.
            </p>
          </div>
          {exhibitCandidates.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              This matter has no documents to exhibit yet.
            </p>
          ) : (
            <div className="space-y-2">
              {exhibitCandidates.map((candidate) => {
                const index = exhibits.findIndex(
                  (exhibit) => exhibit.fileId === candidate.fileId
                );
                const selected = index >= 0;
                return (
                  <div key={candidate.fileId} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`exhibit-${candidate.fileId}`}
                        checked={selected}
                        onCheckedChange={() => toggleExhibit(candidate)}
                      />
                      <Label
                        htmlFor={`exhibit-${candidate.fileId}`}
                        className="font-normal"
                      >
                        {selected && (
                          <span className="mr-2 font-semibold">
                            {exhibitLetter(index)}
                          </span>
                        )}
                        {candidate.name}
                      </Label>
                    </div>
                    {/* The placeholder is "e.g."-prefixed deliberately: the bare
                        phrase reads as a filled value, which makes the "needs a
                        description" warning beside it look like a bug and the
                        disabled Generate button inexplicable. */}
                    {selected && (
                      <Input
                        aria-label={`Description for exhibit ${exhibitLetter(index)}`}
                        placeholder="e.g. the death certificate of the deceased"
                        value={exhibits[index].description}
                        onChange={(event) =>
                          describeExhibit(candidate.fileId, event.target.value)
                        }
                        className="ml-6 w-[calc(100%-1.5rem)]"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {undescribedExhibits.length > 0 && (
            <Banner
              tone="caution"
              title="Every exhibit needs a description"
              line="The certificate identifies the exhibit by what you write here, not by its filename."
            />
          )}
        </div>
      )}

      {/* Said before the button rather than after the fact. One document is kept
          per precedent-and-addressee, so this is a replacement, not an addition
, and the copy it supersedes is archived out of the list. */}
      {replacing && (
        <Banner
          tone="caution"
          title="This replaces the document you already generated"
          line={`You generated ${replacing.templateName}${replacing.recipientName ? ` for ${replacing.recipientName}` : ''} before. Generating again replaces it: the previous document is archived off this matter, including any edits made to it in Word.`}
        />
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          size="xs"
          density="compact"
          variant="outline"
          onClick={onClose}
          disabled={generate.isPending}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          density="compact"
          gap="standard"
          onClick={onGenerate}
          disabled={!canGenerate}
        >
          {generate.isPending ? (
            <>
              <Loader2Icon className="size-4 animate-spin" />
              Generating…
            </>
          ) : (
            <>
              {replacing ? (
                <FileText className="size-4" />
              ) : (
                <FilePlus2 className="size-4" />
              )}
              {replacing ? 'Replace document' : 'Generate'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * The document this generation would replace, if there already is one.
 *
 * The backend's replacement key is `(matter, precedent, addressee)`, so a
 * *fresh* Generate landing on an existing pair overwrites it exactly as
 * regenerating does. Pure, and exported, because it is the half of the
 * self-exhibit guard that decides *what* is being replaced — and the drawer's
 * own Selects cannot be driven under jsdom, so the guard is pinned here rather
 * than through the rendered list.
 */
export function findReplacedDocument(
  existing: PrecedentDocument[],
  templateId: number | null,
  recipientContactId: number | null
): PrecedentDocument | undefined {
  return existing.find(
    (document) =>
      document.documentTemplateId === templateId &&
      document.recipientContactId === recipientContactId
  );
}

/**
 * The matter's documents, minus the one this generation is about to replace.
 *
 * A generated document is an ordinary matter file, so the options endpoint
 * offers it like any other — right in general (a firm may well exhibit a
 * generated inventory in its affidavit) and wrong for exactly one file: the
 * document this generation overwrites. Exhibiting it would make the instrument
 * swear to a superseded draft of itself, and the very call that renders it
 * archives the copy it names.
 */
export function exhibitCandidatesExcludingOwn(
  candidates: PrecedentExhibitOption[],
  replacing: PrecedentDocument | undefined
): PrecedentExhibitOption[] {
  const ownFileId = replacing?.docxFileId ?? null;
  return candidates.filter((candidate) => candidate.fileId !== ownFileId);
}

/**
 * What the drawer was offered, as a key.
 *
 * A fingerprint of the *offer* rather than a fetch timestamp, so a background
 * refetch returning the same precedents and the same documents leaves a
 * half-finished selection alone, while a genuinely different offer resets it.
 */
function offerFingerprint(options: PrecedentDocumentOptions): string {
  return [
    options.precedents.map((option) => option.templateId).join('.'),
    options.recipients.map((option) => option.contactId).join('.'),
    options.exhibitCandidates.map((option) => option.fileId).join('.')
  ].join('|');
}
